/**
 * Configuration file loading and validation
 */

import { readFileSync, existsSync, mkdirSync, writeFileSync, statSync, lstatSync } from "node:fs";
import { join } from "node:path";
import { env, platform } from "node:process";
import { homedir } from "node:os";
import { type Config, validateConfig, getDefaultConfig } from "./schema.js";
import { ConfigurationError } from "../utils/errors.js";
import { logger } from "../utils/logger.js";
import { CONFIG_DIR_NAME, CONFIG_FILE_NAME } from "../utils/constants.js";
import { buildOrgBootstrapConfig, readOrgConfig } from "../setup/org-config.js";

/**
 * Get the default config directory path
 * - Windows: %LOCALAPPDATA%/powerautomate-mcp
 * - macOS: ~/Library/Application Support/powerautomate-mcp
 * - Linux: ~/.config/powerautomate-mcp
 */
export function getConfigDir(): string {
  if (platform === "win32") {
    const localAppData = env["LOCALAPPDATA"];
    if (!localAppData) {
      // Fallback to home directory on Windows if LOCALAPPDATA not set
      return join(homedir(), "AppData", "Local", CONFIG_DIR_NAME);
    }
    return join(localAppData, CONFIG_DIR_NAME);
  }

  if (platform === "darwin") {
    // macOS: ~/Library/Application Support/
    return join(homedir(), "Library", "Application Support", CONFIG_DIR_NAME);
  }

  // Linux and others: ~/.config/
  const xdgConfig = env["XDG_CONFIG_HOME"];
  if (xdgConfig) {
    return join(xdgConfig, CONFIG_DIR_NAME);
  }
  return join(homedir(), ".config", CONFIG_DIR_NAME);
}

/**
 * Get the full path to the config file
 */
export function getConfigPath(): string {
  const customPath = env["PA_CONFIG_PATH"];
  if (customPath) {
    // Validate: must be absolute, no traversal, reasonable length
    if (customPath.length > 500) {
      throw new ConfigurationError("PA_CONFIG_PATH too long (max 500 characters)");
    }
    if (customPath.includes("..")) {
      throw new ConfigurationError("PA_CONFIG_PATH must not contain path traversal (..)");
    }
    if (!customPath.startsWith("/") && !/^[A-Za-z]:[\\/]/.test(customPath)) {
      throw new ConfigurationError("PA_CONFIG_PATH must be an absolute path");
    }
    return customPath;
  }
  return join(getConfigDir(), CONFIG_FILE_NAME);
}

/**
 * Build a full config purely from PA_MCP_* environment variables.
 *
 * Used when no config file exists — the path an MCPB bundle takes, where the
 * client ID, tenant, and environment are supplied through the host app's
 * extension settings UI rather than a config.json. Returns null when the
 * minimum (client ID + environment ID) isn't present, so callers fall back to
 * the normal "config file not found" error.
 *
 * Authentication (the token cache) is still established once via `--setup`;
 * this only removes the need to hand-author a config file.
 */
function buildConfigFromEnv(): Record<string, unknown> | null {
  // Host apps (MCPB) pass unfilled optional settings as empty strings, so treat
  // "" as "not set" throughout.
  const clean = (v: string | undefined): string | undefined => (v && v.trim() ? v.trim() : undefined);
  const clientId = clean(env["PA_MCP_CLIENT_ID"]);
  const environmentId = clean(env["PA_MCP_ENVIRONMENT_ID"]);
  if (!clientId || !environmentId) {
    return null;
  }
  const dataverseUrl = clean(env["PA_MCP_DATAVERSE_URL"]);
  return {
    auth: {
      clientId,
      tenantId: clean(env["PA_MCP_TENANT_ID"]) ?? null,
    },
    environments: {
      // "main" is the env key; "default" points at it (they can't share a name).
      default: "main",
      main: {
        id: environmentId,
        region: clean(env["PA_MCP_ENVIRONMENT_REGION"]) ?? "unitedstates",
        displayName: clean(env["PA_MCP_ENVIRONMENT_NAME"]) ?? "Power Platform",
        ...(dataverseUrl ? { dataverseUrl } : {}),
      },
    },
  };
}

/**
 * True when loadConfig() has ANY source to work from: a config file, a
 * complete PA_MCP_* environment (MCPB/host-managed installs), or an
 * org.json with an environment pin (zero-touch org rollout). The first-run
 * auto-wizard keys off this — running the wizard when one of these sources
 * exists would put readline on stdin, which in MCP server mode is the
 * protocol channel.
 */
export function hasRunnableConfigSource(): boolean {
  return existsSync(getConfigPath()) || buildConfigFromEnv() !== null || buildOrgBootstrapConfig() !== null;
}

/**
 * Load and validate configuration from disk
 * @throws ConfigurationError if config is missing or invalid
 */
export function loadConfig(): Config {
  const configPath = getConfigPath();

  logger.debug({ configPath }, "Loading configuration");

  if (!existsSync(configPath)) {
    // No file — an MCPB/host-managed install can still configure entirely
    // through PA_MCP_* environment variables (extension settings fields).
    const fromEnv = buildConfigFromEnv();
    if (fromEnv) {
      logger.info("No config file found; building configuration from PA_MCP_* environment variables");
      try {
        return validateConfig(fromEnv);
      } catch (err) {
        throw new ConfigurationError(
          `Configuration from environment variables is invalid: ${err instanceof Error ? err.message : String(err)}`,
          err
        );
      }
    }

    // Zero-touch org rollout: an IT-provisioned org.json that pins an
    // environment can boot the server with no per-user config at all — the
    // user's first in-chat sign_in gets them running.
    const fromOrg = buildOrgBootstrapConfig();
    if (fromOrg) {
      logger.info({ source: fromOrg.source }, "No config file found; booting from organization org.json");
      try {
        return validateConfig(fromOrg.config);
      } catch (err) {
        throw new ConfigurationError(
          `Organization config at ${fromOrg.source} is invalid: ${err instanceof Error ? err.message : String(err)}`,
          err
        );
      }
    }

    // An org.json without an environment pin can't boot the server by itself,
    // but it makes --setup nearly zero-touch — say so instead of a dead end.
    const orgHint = readOrgConfig()
      ? `\nAn organization org.json was found (without an environmentId), so setup will need no Client ID: run powerautomate-mcp --setup.`
      : "";
    throw new ConfigurationError(
      `Configuration file not found: ${configPath}\n` +
      `Create the file with your Microsoft Entra app client ID and Power Automate environment(s), ` +
      `or set PA_MCP_CLIENT_ID and PA_MCP_ENVIRONMENT_ID (plus optional PA_MCP_TENANT_ID) in the environment.` +
      orgHint
    );
  }

  // Security: reject symlinks to prevent symlink attacks on config path
  if (platform !== "win32") {
    try {
      const lstats = lstatSync(configPath);
      if (lstats.isSymbolicLink()) {
        throw new ConfigurationError(
          `Configuration file is a symbolic link (potential symlink attack): ${configPath}`
        );
      }
    } catch (err) {
      if (err instanceof ConfigurationError) throw err;
      // lstat failure is non-fatal — continue to read
    }
  }

  // Security: warn if config file is world-readable (non-Windows)
  if (platform !== "win32") {
    try {
      const stats = statSync(configPath);
      const mode = stats.mode & 0o777;
      if (mode & 0o044) {
        logger.warn(
          { configPath, mode: `0o${mode.toString(8)}` },
          "Config file is readable by other users. Run: chmod 600 " + configPath
        );
      }
    } catch {
      // stat failure is non-fatal
    }
  }

  let rawContent: string;
  try {
    rawContent = readFileSync(configPath, "utf-8");
  } catch (err) {
    throw new ConfigurationError(
      `Failed to read configuration file: ${configPath}`,
      err
    );
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawContent);
  } catch (err) {
    throw new ConfigurationError(
      `Invalid JSON in configuration file: ${configPath}`,
      err
    );
  }

  // Interpolate environment variables in string values (e.g. "${CLIENT_ID}")
  parsed = interpolateEnvVars(parsed);

  // Allow env var to override clientId (PA_MCP_CLIENT_ID takes precedence over config file)
  const envClientId = env["PA_MCP_CLIENT_ID"];
  if (envClientId) {
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!UUID_RE.test(envClientId)) {
      throw new ConfigurationError(
        `PA_MCP_CLIENT_ID is not a valid UUID: ${envClientId.substring(0, 50)}`
      );
    }
    (parsed as Record<string, unknown>).auth = {
      ...((parsed as Record<string, Record<string, unknown>>).auth ?? {}),
      clientId: envClientId,
    };
    logger.info("Using client ID from PA_MCP_CLIENT_ID environment variable");
  }

  // Allow env var to override tenantId (settings UI is authoritative over the file)
  const envTenantId = env["PA_MCP_TENANT_ID"];
  if (envTenantId) {
    (parsed as Record<string, unknown>).auth = {
      ...((parsed as Record<string, Record<string, unknown>>).auth ?? {}),
      tenantId: envTenantId,
    };
    logger.info("Using tenant ID from PA_MCP_TENANT_ID environment variable");
  }

  try {
    const config = validateConfig(parsed);
    logger.info(
      {
        environments: Object.keys(config.environments).filter(k => k !== "default"),
        defaultEnv: config.environments.default
      },
      "Configuration loaded"
    );
    return config;
  } catch (err) {
    throw new ConfigurationError(
      `Configuration validation failed: ${err instanceof Error ? err.message : String(err)}`,
      err
    );
  }
}

/**
 * Initialize config directory with a default config file
 * Used for first-time setup
 */
export function initializeConfig(): string {
  const configDir = getConfigDir();
  const configPath = join(configDir, CONFIG_FILE_NAME);

  if (!existsSync(configDir)) {
    mkdirSync(configDir, { recursive: true });
    logger.info({ configDir }, "Created configuration directory");
  }

  if (existsSync(configPath)) {
    logger.warn({ configPath }, "Configuration file already exists, not overwriting");
    return configPath;
  }

  const defaultConfig = getDefaultConfig();
  const content = JSON.stringify(defaultConfig, null, 2);

  writeFileSync(configPath, content, { encoding: "utf-8", mode: 0o600 });
  logger.info({ configPath }, "Created default configuration file");

  return configPath;
}

/**
 * Resolved environment info
 */
export interface ResolvedEnvironment {
  name: string;
  id: string;
  region: string;
  displayName: string | undefined;
  dataverseUrl?: string;
}

/**
 * Resolve an environment name/alias to its configuration
 * @param config - The loaded configuration
 * @param envNameOrId - Environment name, alias, or GUID (optional, uses default if not provided)
 */
export function resolveEnvironment(
  config: Config,
  envNameOrId?: string
): ResolvedEnvironment {
  const envKey = envNameOrId ?? config.environments.default;

  // Check if it's a known environment alias
  const envConfig = config.environments[envKey];
  if (envConfig && typeof envConfig === "object" && "id" in envConfig) {
    return {
      name: envKey,
      id: envConfig.id,
      region: envConfig.region,
      displayName: envConfig.displayName,
      dataverseUrl: envConfig.dataverseUrl,
    };
  }

  // Treat as a direct environment GUID
  if (!envNameOrId) {
    throw new ConfigurationError(
      `Environment "${envKey}" not found in configuration. ` +
      `Available environments: ${Object.keys(config.environments).filter(k => k !== "default").join(", ") || "(none)"}`
    );
  }

  // Assume direct GUID — region defaults to unitedstates
  return {
    name: envNameOrId,
    id: envNameOrId,
    region: "unitedstates",
    displayName: undefined,
  };
}

/**
 * Recursively interpolate ${ENV_VAR} patterns in config values.
 * Only replaces in string values; objects/arrays are traversed recursively.
 */
function interpolateEnvVars(value: unknown): unknown {
  if (typeof value === "string") {
    return value.replace(/\$\{([^}]+)\}/g, (_match, varName: string) => {
      const envValue = env[varName];
      if (envValue === undefined) {
        logger.warn({ varName }, "Environment variable referenced in config is not set");
        return "";
      }
      return envValue;
    });
  }
  if (Array.isArray(value)) {
    return value.map(interpolateEnvVars);
  }
  if (value !== null && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      result[key] = interpolateEnvVars(val);
    }
    return result;
  }
  return value;
}
